import { Link } from "@mui/material";

import { faqs, howItWorks, AnalyticsHeader } from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const logoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL_2025/Pittsburgh%20Penguins/penguins-logo.png";
const Background =
  "https://ss-leagues.s3.amazonaws.com/NHL_2025/Pittsburgh%20Penguins/Penguins_Attend_Flex_Web_Background.JPG";
const Banner =
  "https://ss-leagues.s3.amazonaws.com/NHL_2025/Pittsburgh%20Penguins/Penguins_Attend_Flex_Web_Banner.jpg";
const seatMap3dPreviewImage =
  "https://ss-leagues.s3.amazonaws.com/NHL_2025/Pittsburgh%20Penguins/pittsburghpenguins-seatmap-preview.jpg";

const config: TeamConfig = {
  teamSchema: "pittsburghpenguins_flex",
  browserTitle: "Pittsburgh Penguins Flex Plans",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Pittsburgh Penguins",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10214-penguins-sec",
    mapId: "blockmap2d",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,

    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
    isPerksEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  shouldCheckZipCode: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/penguins/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    logoNameImage: "",
    seatMap3dPreviewImage,
    faviconImage: logoImage,
    findGamesBannerImage: Banner,
    authBackgroundImage: Background,
    checkoutStatusPageImage: Background,
    logoImage: `${logoImage}?width=64&height=64`,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideCheckoutCartBalance: true,
    customAdaLabel: (
      <>
        For accessible seating, please call the Penguins Customer Service
        Department at{" "}
        <Link
          href="tel:4126421842"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (412) 642-1842
        </Link>{" "}
        or email{" "}
        <Link
          href="mailto:cservice@pittsburghpenguins.com"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          cservice@pittsburghpenguins.com
        </Link>
        .
      </>
    ),

    gameFiltersSettings: {
      hideGameFiltersOnFindGames: true,
    },

    eventCardSettings: {
      customTagSize: "150px",
      customMarqueeMinLength: 20,
    },
  },

  colors: {
    primary: "#000000",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Add more events or select Continue to view seating options.",
      confirmEventSelectionLossTitle: "Upgrade Your Pack",
      allUpcomingEventsTitle: "Select 3 Games",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Thank you for your Pittsburgh Penguins purchase!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
